import memoize from 'memoize-one';
import type { ReactNode } from 'react';
import { useContext, useEffect, useMemo } from 'react';
import type { IntlShape } from 'react-intl-next';
import { useIntl } from 'react-intl-next';

import type { AnalyticsReactContextInterface } from '@atlaskit/analytics-next';
import { AnalyticsReactContext } from '@atlaskit/analytics-next';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ExtensionManifest, ExtensionProvider } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import type { EditorActions, EditorProps } from '@atlaskit/editor-core';
import type { EditorContentMode } from '@atlaskit/editor-common/types';
import dropboxExtension from '@atlaskit/editor-extension-dropbox';
import type FeatureFlagClient from '@atlaskit/feature-flag-client';
import type { MentionProvider } from '@atlaskit/mention/resource';

import { manifest as linkCreateExtension } from '@atlassian/editor-extension-link-create';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	connectChartExtensionProvider,
	referentialityExtensionProvider,
} from '@confluence/app-referentiality/entry-points/providers.ts';
import { useSmartButtonPremiumGate } from '@confluence/automation-extensions/entry-points/useSmartButtonPremiumGate.ts';
import { smartButtonExtensionProvider } from '@confluence/automation-extensions/entry-points/SmartButtonManifest.ts';
import { snippetExtensionProvider } from '@confluence/snippets/entry-points/snippetExtensionProvider.ts';
import { getSnippetsStateSnapshot } from '@confluence/snippets/entry-points/getSnippetsStateSnapshot.ts';
import { registerSnippetsQuickInsertOnLoad } from '@confluence/snippets/entry-points/registerSnippetsQuickInsertOnLoad.ts';
import type { Snippet } from '@confluence/snippets/entry-points/snippetTypes.ts';
import type { ExtensionSource } from '@confluence/change-edition/entry-points/PremiumExtensionsUpsell.tsx';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { projectLinkerExtensionProvider } from '@confluence/create-space/entry-points/projectLinkerExtensionProvider.ts';
import { createPremiumExtensionManifests } from '@confluence/custom-sites-extensions/entry-points/createPremiumExtensionManifests.ts';
import { usePremiumGate } from '@confluence/custom-sites-extensions/entry-points/usePremiumGate.ts';
import type { SubscriptionHandlerFnType } from '@confluence/editor-features/entry-points/SubscriptionHandlerFnType.ts';
import { DROPBOX_APP_KEY } from '@confluence/editor-features/entry-points/DROPBOX_APP_KEY.ts';
import { useDocChangeContext } from '@confluence/editor-features/entry-points/useDocChangeContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import { getOptedOutMacros } from '@confluence/editor-features/entry-points/featureFlagsUtils.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useShouldHideOverlay } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/useShouldHideOverlay.ts';
import type { ConfluencePageContext } from '@confluence/fabric-extension-lib/entry-points/ConfluencePageContext.ts';
import { getConfluenceMacrosExtensionProviderForEditor } from '@confluence/fabric-extension-lib/entry-points/getConfluenceMacrosExtensionProviderForEditor.ts';
import type { OnCompleteCallbackFnType } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';
import type { MacroConfig } from '@confluence/fabric-extension-lib/entry-points/extension-provider.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useIsAdmin } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetContextToken } from '@confluence/forge-ui/entry-points/useContextToken.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import {
	useRouteActions,
	type RouteStateType,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	setInEditorTemplatesPanelQueryParam,
	IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
	CONTEXT_PANEL_TAB_TARGET_BLOCK,
} from '@confluence/template-utils/entry-points/urlUtils.ts';
import { useEmbeddedEditPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/edit-page.ts';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import { getFullPageExtensionHandlers } from './FullPageMacroExtensionHandler.tsx';
import { getForgeExtensionHandler } from './getForgeExtensionHandler.ts';
import { type EditorAPI } from './extensionHandlerTypes.ts';

type UseExtensionOptionsProps = {
	contentId: string;
	contentType: string;
	editorActions: EditorActions;
	mentionProvider: Promise<MentionProvider>;
	spaceKey: string;
	spaceId?: string;
	contentMode?: EditorContentMode;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	openConnectionsPanel?: (localId: string) => void;
	openConfigPanel?: (localId: string) => void;
	openSmartButtonConfigPanel?: (localId: string) => void;
	onCompleteCallback?: OnCompleteCallbackFnType;
	isLivePage?: boolean;
	allowCreateDatabaseExtension?: boolean;
	allowDatabaseQuickInsert?: boolean;
	allowCreateWhiteboardExtension?: boolean;
	flags?: FlagsStateContainer;
	aiOptInStatus?: 'enabled' | 'disabled-opt-in' | 'disabled';
	editorAPI: EditorAPI | undefined;
	setUpsellModalSource?: (source: ExtensionSource) => void;
	isFullPageEditor?: boolean;
	allowLegacyMacros?: boolean;
	allowCompanyHubExclusiveExtensions?: boolean;
	isLivePagesFeatureEnabled?: boolean;
	isCollabEditForLiveUpdatesEnabled?: boolean;
	pageTreeStateUpdater?: PageTreeStateUpdater;
};

const noop = () => {};

const _getClassicEditorProviders = memoize(
	({
		cloudId,
		contentId,
		contentType,
		spaceKey,
		spaceId,
		contentMode,
		editorActions,
		createAnalyticsEvent,
		mentionProvider,
		intlNext,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		setActiveModal,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		closeActiveModal,
		getMigrationExtensionManifest,
		extensionContextProvider,
		subscribeDocChange,
		unsubscribeDocChange,
		onCompleteCallback,
		analyticsContext,
		isLivePage,
		allowCreateDatabaseExtension,
		allowDatabaseQuickInsert,
		allowCreateWhiteboardExtension,
		flags,
		editorAPI,
		setUpsellModalSource,
		hideGlassPaneForForge,
		isFullPageEditor,
		allowLegacyMacros,
		allowCompanyHubExclusiveExtensions,
		isLivePagesFeatureEnabled,
		isCollabEditForLiveUpdatesEnabled,
		setQueryParams,
		isBlockTemplateQuickInsertEnabled,
		snippets,
		pageTreeStateUpdater,
	}: {
		cloudId: string;
		createAnalyticsEvent: CreateUIAnalyticsEvent;
		openMacroBrowserToEditExistingMacro?: Function;
		openMacroBrowserForInitialConfiguration?: Function;
		setActiveModal?: (component: ReactNode, props: any) => void;
		closeActiveModal?: () => void;
		getMigrationExtensionManifest?: Function;
		intlNext: IntlShape;
		featureFlagClient: FeatureFlagClient;
		subscribeDocChange: SubscriptionHandlerFnType;
		unsubscribeDocChange: SubscriptionHandlerFnType;
		analyticsContext: AnalyticsReactContextInterface;
		hideGlassPaneForForge: boolean;
		isLivePage?: boolean;
		isCollabEditForLiveUpdatesEnabled?: boolean;
		setQueryParams: RouteStateType['setQueryParams'];
		isBlockTemplateQuickInsertEnabled: boolean;
		snippets: Snippet[];
		pageTreeStateUpdater?: PageTreeStateUpdater;
	} & UseExtensionOptionsProps) => {
		const experienceTracker = useContext(ExperienceTrackerContext);
		const { isSmartButtonAllowed } = useSmartButtonPremiumGate();
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
		const pageContext: ConfluencePageContext = {
			contentId,
			spaceKey,
			spaceId,
			features: {
				configPanel: {
					enabled: true,
					optedOut: getOptedOutMacros(),
				},
				elementBrowser: true,
			},
		};

		const extensionHandlers = getFullPageExtensionHandlers({
			contentId,
			spaceKey,
			extensionContextProvider,
			editorActions,
			subscribeDocChange,
			unsubscribeDocChange,
			isLivePage,
			isCollabEditForLiveUpdatesEnabled,
		});

		const macroConfig: MacroConfig = {
			contentId,
			setActiveModal,
			closeActiveModal,
		};

		const hideCreateContentMacros = isCompanyHub && fg('company-hub-hide-create-content-macros');
		const confluenceMacroExtensionProviderForEditor = getConfluenceMacrosExtensionProviderForEditor(
			pageContext,
			extensionHandlers,
			editorAPI ?? undefined,
			editorActions,
			macroConfig,
			getMigrationExtensionManifest || noop,
			openMacroBrowserToEditExistingMacro || noop,
			openMacroBrowserForInitialConfiguration || noop,
			intlNext,
			createAnalyticsEvent,
			onCompleteCallback,
			isLivePage,
			flags,
			experienceTracker,
			contentType,
			allowCreateDatabaseExtension && !hideCreateContentMacros,
			allowDatabaseQuickInsert,
			allowCreateWhiteboardExtension && !hideCreateContentMacros,
			allowLegacyMacros,
			isLivePagesFeatureEnabled,
			isCompanyHub,
			pageTreeStateUpdater,
		);

		const extensionProvidersList: (Promise<ExtensionProvider> | ExtensionProvider)[] = [];
		extensionProvidersList.push(confluenceMacroExtensionProviderForEditor);

		const extensions = [] as ExtensionManifest[];
		extensions.push(
			dropboxExtension({
				appKey: DROPBOX_APP_KEY,
				canMountinIframe: false,
			}),
		);

		if (!hideCreateContentMacros) {
			extensions.push(
				linkCreateExtension({
					intl: intlNext,
					defaultCloudId: cloudId,
					defaultFormValues: {
						'confluence-page': {
							parentId: contentId,
							spaceId,
						},
					},
					analyticsContext,
					pageContext: {
						isLivePage,
						isBlogpost: contentType === 'blogpost',
						isFullPageEditor,
					},
					location: 'confluence-page',
					allowConfluenceEditViewEmbed: true,
					flags: {
						showSuccess: ({ title, description, actions }) => {
							void flags?.showSuccessFlag({
								title,
								description,
								actions,
								isAutoDismiss: true,
							});
						},
						showFailure: ({ title, description, actions }) => {
							void flags?.showErrorFlag({
								title,
								description,
								actions,
								isAutoDismiss: true,
							});
						},
					},
				}),
			);
		}

		extensionProvidersList.push(new DefaultExtensionProvider(extensions));

		const { edition } = useSessionData();
		const isPremium = edition === ConfluenceEdition.PREMIUM;
		const isAdmin = useIsAdmin();
		const showPremiumExtensions = isAdmin || isPremium;

		const premiumExtensions = showPremiumExtensions
			? createPremiumExtensionManifests({
					contentId,
					editorActions,
					openConfigPanel,
					intl: intlNext,
					setUpsellModalSource,
					allowCompanyHubExclusiveExtensions,
				})
			: [];

		extensionProvidersList.push(new DefaultExtensionProvider(premiumExtensions));

		// Don't add the Smart Button extension if we're in the Company Hub,
		// the company-hub-migration-prevent-1p-macro experiment is enabled,
		// and the company-hub-prevent-1p-macro-via-tap-trait gate returns false.
		// company-hub-prevent-1p-macro-via-tap-trait is controlled by a tap trait that checks if a tenant's company hub
		// has been published with unsupported 1p macros in the last 4 months,
		// in which case we will allow them to insert Smart Button, an unsupported 1p macro.
		if (
			!isCompanyHub ||
			!expVal('company-hub-migration-prevent-1p-macro', 'isEnabled', false) ||
			fg('company-hub-prevent-1p-macro-via-tap-trait')
		) {
			extensionProvidersList.push(
				smartButtonExtensionProvider({
					contentId,
					editorActions,
					openSmartButtonConfigPanel,
					intl: intlNext,
					isSmartButtonAllowed,
					contentType,
				}),
			);
		}

		extensionProvidersList.push(
			projectLinkerExtensionProvider({
				cloudId,
				intl: intlNext,
				editorActions,
				createAnalyticsEvent,
			}),
		);

		extensionProvidersList.push(
			connectChartExtensionProvider(
				intlNext,
				editorActions,
				openConnectionsPanel,
				createAnalyticsEvent,
			),
		);

		extensionProvidersList.push(
			referentialityExtensionProvider({
				intl: intlNext,
				openConnectionsPanel,
				createAnalyticsEvent,
			}),
		);

		if (expVal('cc_snippets', 'isEnabled', false)) {
			const shouldAddBlockTemplateQuickInsert =
				isBlockTemplateQuickInsertEnabled && expVal('cc_snippets', 'isEnabled', false);
			const onOpenTemplatePanel = () => {
				if (setQueryParams) {
					setInEditorTemplatesPanelQueryParam(
						setQueryParams,
						IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN,
						CONTEXT_PANEL_TAB_TARGET_BLOCK,
					);
				}
			};
			extensionProvidersList.push(
				snippetExtensionProvider(
					intlNext,
					snippets,
					shouldAddBlockTemplateQuickInsert ? onOpenTemplatePanel : undefined,
				),
			);
		}

		extensionProvidersList.push(
			getForgeExtensionHandler({
				spaceKey,
				spaceId,
				contentId,
				contentType,
				contentMode,
				mentionProvider,
				isEditing: true,
				connectExtensionProvider: confluenceMacroExtensionProviderForEditor,
				editorActions,
				hideGlassPaneForForge,
				locale: intlNext.locale,
				isLivePage: !!isLivePage,
				editorAPI,
				useGetContextToken,
			}),
		);

		return extensionProvidersList;
	},
);

const _getExtensionRelatedProps = memoize(
	({
		cloudId,
		contentId,
		contentType,
		contentMode,
		editorActions,
		createAnalyticsEvent,
		mentionProvider,
		spaceKey,
		spaceId,
		// Props from ClassicEditorContext
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		setActiveModal,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		closeActiveModal,
		getMigrationExtensionManifest,
		extensionContextProvider,
		intlNext,
		featureFlagClient,
		subscribeDocChange,
		unsubscribeDocChange,
		onCompleteCallback,
		analyticsContext,
		isLivePage,
		allowCreateDatabaseExtension,
		allowDatabaseQuickInsert,
		allowCreateWhiteboardExtension,
		flags,
		aiOptInStatus,
		editorAPI,
		setUpsellModalSource,
		hideGlassPaneForForge,
		isFullPageEditor,
		allowLegacyMacros,
		allowCompanyHubExclusiveExtensions,
		isLivePagesFeatureEnabled,
		isCollabEditForLiveUpdatesEnabled,
		setQueryParams,
		isBlockTemplateQuickInsertEnabled,
		snippets,
		pageTreeStateUpdater,
	}: Parameters<typeof _getClassicEditorProviders>[0]) => {
		const extensionProviders = _getClassicEditorProviders({
			cloudId,
			contentId,
			contentType,
			contentMode,
			spaceKey,
			spaceId,
			editorActions,
			createAnalyticsEvent,
			mentionProvider,
			intlNext,
			featureFlagClient,
			openMacroBrowserToEditExistingMacro,
			openMacroBrowserForInitialConfiguration,
			setActiveModal,
			openConnectionsPanel,
			openConfigPanel,
			openSmartButtonConfigPanel,
			closeActiveModal,
			getMigrationExtensionManifest,
			extensionContextProvider,
			subscribeDocChange,
			unsubscribeDocChange,
			onCompleteCallback,
			analyticsContext,
			isLivePage,
			allowCreateDatabaseExtension,
			allowDatabaseQuickInsert,
			allowCreateWhiteboardExtension,
			flags,
			snippets,
			aiOptInStatus,
			editorAPI,
			setUpsellModalSource,
			hideGlassPaneForForge,
			isFullPageEditor,
			allowLegacyMacros,
			allowCompanyHubExclusiveExtensions,
			isLivePagesFeatureEnabled,
			isCollabEditForLiveUpdatesEnabled,
			setQueryParams,
			isBlockTemplateQuickInsertEnabled,
			pageTreeStateUpdater,
		});

		const allowExtension: EditorProps['allowExtension'] = {
			allowBreakout: true,
			allowExtendFloatingToolbars: true,
		};

		return {
			allowExtension,
			extensionProviders,
		};
	},
);

export const useExtensionOptions = ({
	contentId,
	contentType,
	editorActions,
	mentionProvider,
	spaceKey,
	spaceId,
	contentMode,
	extensionContextProvider,
	openConnectionsPanel,
	openConfigPanel,
	openSmartButtonConfigPanel,
	onCompleteCallback,
	isLivePage,
	allowCreateDatabaseExtension,
	allowDatabaseQuickInsert,
	allowCreateWhiteboardExtension,
	flags,
	aiOptInStatus,
	editorAPI,
	setUpsellModalSource,
	isFullPageEditor = false,
	allowLegacyMacros = false,
	allowCompanyHubExclusiveExtensions = false,
	isLivePagesFeatureEnabled,
	isCollabEditForLiveUpdatesEnabled,
	pageTreeStateUpdater,
}: UseExtensionOptionsProps) => {
	const {
		openMacroBrowserForInitialConfiguration,
		openMacroBrowserToEditExistingMacro,
		loadMacroBrowserResources,
		setActiveModal,
		closeActiveModal,
		getMigrationExtensionManifest,
		getExtensionHandlers,
		populateExtensionProviders,
	} = useContext(ClassicEditorContext);
	const { cloudId, featureFlagClient } = useSessionData();
	const shouldShowPremiumUpsell = usePremiumGate();
	const intlNext = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setQueryParams } = useRouteActions();

	// Trigger loading of macro browser resources
	useEffect(() => {
		if (loadMacroBrowserResources) {
			loadMacroBrowserResources();
		}
	}, [loadMacroBrowserResources]);

	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();

	const analyticsContext = useContext(AnalyticsReactContext);

	const { isTemplateBrowserEnabled } = useEmbeddedEditPageFeatures();
	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();

	// Enable block template quick insert when we are not in embedded Confluence or when in embedded Confluence andthe template browser is enabled
	const isBlockTemplateQuickInsertEnabled = !isEmbeddedConfluence || !!isTemplateBrowserEnabled;

	// Get snippets snapshot without subscribing to avoid re-renders
	const {
		snippets,
		isLoading: snippetsLoading,
		isLoaded: snippetsLoaded,
	} = getSnippetsStateSnapshot();

	// If snippets are still loading, register a callback to add them to quick insert when done
	useEffect(() => {
		if (snippetsLoading && !snippetsLoaded && editorAPI) {
			registerSnippetsQuickInsertOnLoad(editorAPI);
		}
		// Only watch editorAPI - we check the loading state from the snapshot at render time
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorAPI]);

	// TODO: Currently, Forge macros can only be non-bodied, but there's work
	// underway to allow for bodied macros. When that's complete, we'll need
	// to update this to be contingent on the macro type.
	const hideGlassPaneForForge = useShouldHideOverlay('forge', false);

	const extensionOptions = useMemo(
		() =>
			_getExtensionRelatedProps({
				cloudId,
				contentId,
				contentType,
				contentMode,
				editorActions,
				createAnalyticsEvent,
				mentionProvider,
				spaceKey,
				spaceId,
				openMacroBrowserForInitialConfiguration,
				openMacroBrowserToEditExistingMacro,
				setActiveModal,
				openConnectionsPanel,
				openConfigPanel,
				openSmartButtonConfigPanel,
				closeActiveModal,
				getMigrationExtensionManifest,
				extensionContextProvider,
				intlNext,
				featureFlagClient,
				subscribeDocChange,
				unsubscribeDocChange,
				onCompleteCallback,
				analyticsContext,
				isLivePage,
				allowCreateDatabaseExtension,
				allowDatabaseQuickInsert,
				allowCreateWhiteboardExtension,
				flags,
				aiOptInStatus,
				editorAPI,
				setUpsellModalSource: shouldShowPremiumUpsell ? setUpsellModalSource : undefined,
				hideGlassPaneForForge,
				isFullPageEditor,
				allowLegacyMacros,
				allowCompanyHubExclusiveExtensions,
				isLivePagesFeatureEnabled,
				isCollabEditForLiveUpdatesEnabled,
				setQueryParams,
				isBlockTemplateQuickInsertEnabled,
				snippets,
				pageTreeStateUpdater,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, spaceKey, editorAPI, spaceId, pageTreeStateUpdater],
	);

	return {
		extensionOptions,
		rendererExtensionOptions: {
			getExtensionHandlers,
			populateExtensionProviders,
		},
	};
};
